import React, { useContext } from 'react';
import { Form } from 'react-final-form';
import createDecorator from 'final-form-focus';
import { FORM_ERROR } from 'final-form';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';
import { Heading1, Heading3, Body, CaptionMixin } from '@qover/typography';
import { InputField, PrimaryButton } from '@qover/molecules';
import { BrokerContext } from '@qover/contexts';
import { FormattedMarkdown } from '@qover/react-intl';
import { Modal } from './Modal';
import {
  createValidator,
  email as emailValidation,
  required,
} from '@qover/utils/src/validation';

const focusOnError = createDecorator();

const Line = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
  margin: 0 auto 10px;
  &.input {
    max-width: 350px;
  }
`;

const ModalContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const CloseButton = styled.div`
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textQuote()};
  margin: 0 0 20px 0;
`;

const ModalDescription = styled(Body)`
  margin: 5px 0;
`;

const ErrorMessage = styled(Heading3)`
  color: ${({ theme }) => theme.colors.error()};
`;

const Agreement = styled.div`
  ${CaptionMixin};
  color: ${({ theme }) => theme.colors.textQuote()};
  margin-top: 50px;

  a,
  a:visited {
    color: ${({ theme }) => theme.colors.textQuote()};
  }
`;

const formValidation = createValidator({
  email: [required, emailValidation],
});

type EmailModalProps = {
  onSubmit: (form: any) => Promise<void>;
  onClose: () => void;
  email?: string;
  disabled?: boolean;
};

type EmailForm = {
  email: string;
};

export const EmailModal: React.FC<EmailModalProps> = props => {
  const broker = useContext(BrokerContext);
  const privacyPolicyLink =
    broker?.informations?.links?.privacy ??
    'https://www.qover.com/terms-and-policies#parentVerticalTab4';

  const onSubmit = (form: EmailForm) =>
    props.onSubmit(form).catch(() => ({
      [FORM_ERROR]: true,
    }));

  const emailForm: EmailForm = { email: props.email ?? '' };

  return (
    <Modal hideOverflow onClickOutside={props.onClose}>
      <Container>
        <CloseButton
          data-test="general.emailModalClose"
          onClick={props.onClose}
        >
          <Trans id="general.close" />
        </CloseButton>
      </Container>
      <ModalContainer>
        <Form
          // @ts-ignore
          decorators={[focusOnError]}
          validate={formValidation}
          // @ts-ignore
          onSubmit={onSubmit}
          // @ts-ignore
          initialValues={emailForm}
        >
          {({ handleSubmit, submitSucceeded, hasSubmitErrors }) => (
            <>
              {submitSucceeded && (
                <ModalContainer style={{ textAlign: 'center' }}>
                  <Trans id="general.emailModalThanks" />
                </ModalContainer>
              )}
              {hasSubmitErrors && (
                <ErrorMessage className="font-bold text-center">
                  <Trans id="general.emailModalError" />
                </ErrorMessage>
              )}
              {!submitSucceeded && !hasSubmitErrors && (
                <>
                  <Heading1 className="text-center">
                    <Trans id="general.emailModalTitle" />
                  </Heading1>
                  <form onSubmit={handleSubmit} className="subscribe-form">
                    <Line className="input">
                      <ModalDescription as="span">
                        <Trans id="general.emailDescription" />
                      </ModalDescription>
                      <InputField
                        autoComplete="email"
                        autoCapitalize="off"
                        autoCorrect="off"
                        data-test="emailModal.email"
                        id="modal.email"
                        inputMode="email"
                        label={<Trans id="general.email" />}
                        name="email"
                        type="text"
                        parse={value => value && value.trim()}
                        disabled={props.disabled}
                      />
                    </Line>
                    <Line className="input">
                      <PrimaryButton
                        data-test="general.emailModalSubmit"
                        type="submit"
                      >
                        <Trans id="general.emailModalSendButton" />
                      </PrimaryButton>
                    </Line>
                    <Line>
                      <Agreement>
                        <FormattedMarkdown
                          id="general.emailModalAgreement"
                          options={{
                            overrides: {
                              a: (p: any) => (
                                <a
                                  {...p}
                                  data-test="general.emailModalPrivacyPolicyLink"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {p.children}
                                </a>
                              ),
                            },
                          }}
                          values={{ link: privacyPolicyLink }}
                        />
                      </Agreement>
                    </Line>
                  </form>
                </>
              )}
            </>
          )}
        </Form>
      </ModalContainer>
    </Modal>
  );
};
